import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { ChartConfig } from '../../api/aiDashboardApi';
import { ModernChartRenderer } from './ModernChartRenderer';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ChartRendererProps {
  config: ChartConfig;
}

export const ChartRenderer: React.FC<ChartRendererProps> = ({ config }) => {
  // Use ModernChartRenderer for advanced chart types
  const advancedChartTypes = ['streamgraph', 'horizon', 'connected-scatter', 'dot-plot', 'slope-chart', 'treemap'];
  
  if (advancedChartTypes.includes(config.type)) {
    return <ModernChartRenderer config={config} />;
  }

  const generateColors = (count: number) => {
    const colors = [
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(16, 185, 129, 0.8)',   // Green
      'rgba(245, 158, 11, 0.8)',   // Yellow
      'rgba(239, 68, 68, 0.8)',    // Red
      'rgba(139, 92, 246, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(14, 165, 233, 0.8)',   // Sky
      'rgba(34, 197, 94, 0.8)',    // Emerald
    ];
    
    return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
  };

  const generateBorderColors = (count: number) => {
    const colors = [
      'rgba(59, 130, 246, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(245, 158, 11, 1)',
      'rgba(239, 68, 68, 1)',
      'rgba(139, 92, 246, 1)',
      'rgba(236, 72, 153, 1)',
      'rgba(14, 165, 233, 1)',
      'rgba(34, 197, 94, 1)',
    ];
    
    return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
  };

  const prepareChartData = () => {
    const labels = config.data.map(item => item.label || item.name || 'Unknown');
    const values = config.data.map(item => item.value || item.count || 0);
    
    const backgroundColors = generateColors(labels.length);
    const borderColors = generateBorderColors(labels.length);

    return {
      labels,
      datasets: [{
        label: config.title,
        data: values,
        backgroundColor: backgroundColors,
        borderColor: borderColors,
        borderWidth: 2,
        fill: config.type === 'area',
        tension: 0.4,
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
        }
      },
      title: {
        display: true,
        text: config.title,
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        padding: 20,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y || context.parsed;
            
            if (config.aggregation === 'sum' && config.title.toLowerCase().includes('cost')) {
              return `${label}: $${value.toLocaleString()}`;
            }
            return `${label}: ${value.toLocaleString()}`;
          }
        }
      }
    },
    scales: config.type === 'pie' || config.type === 'doughnut' ? {} : {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          callback: (value: any) => {
            if (config.aggregation === 'sum' && config.title.toLowerCase().includes('cost')) {
              return `$${value.toLocaleString()}`;
            }
            return value.toLocaleString();
          }
        }
      }
    }
  };

  const renderChart = () => {
    const chartData = prepareChartData();

    switch (config.type) {
      case 'bar':
        return <Bar data={chartData} options={chartOptions} />;
      case 'line':
      case 'area':
        return <Line data={chartData} options={chartOptions} />;
      case 'pie':
        return <Pie data={chartData} options={chartOptions} />;
      case 'doughnut':
        return <Doughnut data={chartData} options={chartOptions} />;
      default:
        return <Bar data={chartData} options={chartOptions} />;
    }
  };

  // Generate sample data if no data is available
  if (!config.data || config.data.length === 0) {
    const sampleData = [
      { label: 'Sample A', value: 45, count: 45 },
      { label: 'Sample B', value: 32, count: 32 },
      { label: 'Sample C', value: 28, count: 28 },
      { label: 'Sample D', value: 15, count: 15 },
      { label: 'Sample E', value: 8, count: 8 }
    ];
    config = { ...config, data: sampleData };
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 group">
      <div className="h-80">
        {renderChart()}
      </div>
      
      {/* Data Summary */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>Total Records: {config.data.length}</span>
          <span>
            {config.aggregation === 'sum' && config.title.toLowerCase().includes('cost') 
              ? `Total: $${config.data.reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString()}`
              : `Total: ${config.data.reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString()}`
            }
          </span>
        </div>
      </div>
    </div>
  );
};